import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { User, UpdateUserRequest } from "../../types/user";
import { useAccessManagementService } from "../../services/accessManagementService";
import { useApiService } from "../../services/apiService";
import { TenantDto, AppRoleDto } from "../../types/accessManagement";
import { FeatureDto as AppFeatureDto, useTenantManagementService } from "../../services/tenantManagementService";
import { darkDatePickerSlotProps } from "../../themes/datePickerStyles";
import { parseDate } from "../../utils/dateUtils";

interface UserFormDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (userId: string, updates: UpdateUserRequest) => Promise<void>;
  mode?: "add" | "edit";
  availableUsers?: Array<{ id: string; entraObjectId: string; email: string; displayName: string; isEnabled: boolean }>;
  currentTenantId?: string | null;
  isActingAsGlobalAdmin?: boolean;
  isLoggedInGlobalAdmin?: boolean;
  currentUserId?: string | null;
}

type TenantRoleMappingRow = {
  id: string;
  tenantId: string;
  role: string;
  isEnabled: boolean;
  isNew: boolean;
  isDeleted: boolean;
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  if (value !== index) return null;

  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

const a11yProps = (index: number) => ({
  id: `user-form-tab-${index}`,
  "aria-controls": `user-form-tabpanel-${index}`,
});

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  user,
  onClose,
  onSave,
  mode = "edit",
  availableUsers = [],
  currentTenantId = null,
  isActingAsGlobalAdmin = true,
  isLoggedInGlobalAdmin = false,
  currentUserId = null,
}) => {
  const { getTenants, getRoles, addUser, getUserFeatureOverrides, getUserAirframeOverrides } = useAccessManagementService();
  const { getAvailableFeatures, getTenantFeatureKeys } = useTenantManagementService();
  const apiService = useApiService();
  const isInTenantMode = !isActingAsGlobalAdmin && !!currentTenantId;

  const [activeTab, setActiveTab] = useState(0);

  const [formData, setFormData] = useState<User | null>(user);
  const [isSaving, setIsSaving] = useState(false);

  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [roles, setRoles] = useState<AppRoleDto[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<AppFeatureDto[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [selectedRoleKey, setSelectedRoleKey] = useState<string>("");

  const [mappingRows, setMappingRows] = useState<TenantRoleMappingRow[]>([]);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  const [globalAdminChecked, setGlobalAdminChecked] = useState(false);
  const initialGlobalAdminChecked = useRef(false);
  const [isGlobalAdminRowRemoved, setIsGlobalAdminRowRemoved] = useState(false);

  const [startDateUtcInput, setStartDateUtcInput] = useState<Date | null>(null);
  const [endDateUtcInput, setEndDateUtcInput] = useState<Date | null>(null);

  const [selectedAllowedFeatures, setSelectedAllowedFeatures] = useState<Set<string>>(new Set());
  const [addModeEnabledFeatureKeys, setAddModeEnabledFeatureKeys] = useState<string[]>([]);
  const [loadingAddModeTenantFeatures, setLoadingAddModeTenantFeatures] = useState(false);

  const [selectedAllowedAirframes, setSelectedAllowedAirframes] = useState<Set<string>>(new Set());
  const [addModeEnabledTailNumbers, setAddModeEnabledTailNumbers] = useState<string[]>([]);
  const [loadingAddModeTailNumbers, setLoadingAddModeTailNumbers] = useState(false);

  type TenantFeatureSectionState = {
    tenantId: string;
    tenantName: string;
    enabledFeatureKeys: string[];
    selectedAllowedFeatureKeys: Set<string>;
  };

  const [tenantFeatureSections, setTenantFeatureSections] = useState<Record<string, TenantFeatureSectionState>>({});
  const [loadingTenantFeatures, setLoadingTenantFeatures] = useState(false);
  const [tenantFeaturesError, setTenantFeaturesError] = useState<string | null>(null);

  type TenantAirframeSectionState = {
    tenantId: string;
    tenantName: string;
    enabledTailNumbers: string[];
    selectedAllowedTailNumbers: Set<string>;
  };

  const [tenantAirframeSections, setTenantAirframeSections] = useState<Record<string, TenantAirframeSectionState>>({});
  const [loadingTenantAirframes, setLoadingTenantAirframes] = useState(false);
  const [tenantAirframesError, setTenantAirframesError] = useState<string | null>(null);

  type TailNumbersResponse = {
    data?: string[];
    success?: boolean;
    message?: string;
  };

  const getTailNumbersForTenant = useCallback(async (tenantId: string): Promise<string[]> => {
    const normalizedTenantId = (tenantId ?? "").trim();
    if (!normalizedTenantId) return [];

    const response = await apiService.apiCall<TailNumbersResponse>({
      method: "GET",
      endpoint: "metadata/tail-numbers",
      headers: { "X-Tenant-Id": normalizedTenantId },
      requireAuth: true,
    });

    return Array.isArray(response.data?.data) ? response.data.data : [];
  }, [apiService]);

  const isAddMode = mode === "add";
  const isEditMode = !isAddMode;
  const isIdentityFieldsReadOnly = isEditMode;

  // Fetch tenants and roles when dialog opens
  useEffect(() => {
    if (!open) return;

    setActiveTab(0);

    let cancelled = false;

    const loadData = async () => {
      setLoadingDropdowns(true);
      try {
        const [tenantsData, rolesData, featuresData] = await Promise.all([
          getTenants(),
          getRoles(),
          getAvailableFeatures(),
        ]);
        if (!cancelled) {
          setTenants(tenantsData);
          setRoles(rolesData);
          setAvailableFeatures(featuresData);
        }
      } finally {
        if (!cancelled) {
          setLoadingDropdowns(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [open, getTenants, getRoles, getAvailableFeatures]);

  // Reset form data when dialog opens
  useEffect(() => {
    if (isAddMode) {
      setFormData({
        id: "",
        email: "",
        entraObjectId: "",
        displayName: "",
        status: "enabled",
        tenants: [],
      });

      setStartDateUtcInput(null);
      setEndDateUtcInput(null);

      setSelectedTenantId(isInTenantMode && currentTenantId ? currentTenantId : "");
      setSelectedRoleKey("");
      setMappingRows([]);
      setSelectedAllowedFeatures(new Set());
      setAddModeEnabledTailNumbers([]);
      setSelectedAllowedAirframes(new Set());

      setGlobalAdminChecked(false);
      initialGlobalAdminChecked.current = false;
      setIsGlobalAdminRowRemoved(false);
    } else {
      setFormData(user);

      setStartDateUtcInput(parseDate(user?.startDateUtc));
      setEndDateUtcInput(parseDate(user?.endDateUtc));

      setSelectedTenantId("");
      setSelectedRoleKey("");

      const userHasGlobalAdminRole =
        (user?.isGlobalAdmin ?? false) ||
        (user?.tenants ?? []).some((t) => t.role === "GlobalAdmin");

      setGlobalAdminChecked(userHasGlobalAdminRole);
      // Track only the true global-scope assignment state from API
      initialGlobalAdminChecked.current = user?.isGlobalAdmin ?? false;
      setIsGlobalAdminRowRemoved(false);

      let rowsFromUser = (user?.tenants ?? []).map((t) => ({
        id: t.id || crypto.randomUUID(),
        tenantId: t.tenantId,
        role: t.role,
        isEnabled: true,
        isNew: false,
        isDeleted: false,
      }));

      // In tenant mode, show only the current tenant's mapping
      if (isInTenantMode && currentTenantId) {
        const currentTenantRow = rowsFromUser.find((r) => r.tenantId === currentTenantId);
        rowsFromUser = currentTenantRow
          ? [currentTenantRow]
          : [{
              id: crypto.randomUUID(),
              tenantId: currentTenantId,
              role: "",
              isEnabled: true,
              isNew: true,
              isDeleted: false,
            }];
      }

      setMappingRows(rowsFromUser);
    }
  }, [user, open, isAddMode, isInTenantMode, currentTenantId]);

  // In Add mode, show features based on selected tenant
  useEffect(() => {
    if (!open || !isAddMode) return;

    const normalizedRole = (selectedRoleKey ?? "").trim();
    const normalizedTenantId = (selectedTenantId ?? "").trim();

    // GlobalAdmin has no tenant assignment; feature overrides are not applicable
    if (normalizedRole === "GlobalAdmin") {
      setAddModeEnabledFeatureKeys([]);
      setSelectedAllowedFeatures(new Set());
      setLoadingAddModeTenantFeatures(false);
      return;
    }

    // No tenant selected yet
    if (!normalizedTenantId) {
      setAddModeEnabledFeatureKeys([]);
      setSelectedAllowedFeatures(new Set());
      setLoadingAddModeTenantFeatures(false);
      return;
    }

    // Reset immediately on tenant change to avoid stale state
    setAddModeEnabledFeatureKeys([]);
    setSelectedAllowedFeatures(new Set());

    let cancelled = false;

    const load = async () => {
      setLoadingAddModeTenantFeatures(true);
      try {
        const keys = await getTenantFeatureKeys(normalizedTenantId);
        if (cancelled) return;

        const normalizedKeys = (keys ?? [])
          .map((k) => (k ?? "").trim())
          .filter((k) => Boolean(k));

        const uniqueSortedKeys = Array.from(new Set(normalizedKeys)).sort((a, b) => a.localeCompare(b));

        setAddModeEnabledFeatureKeys(uniqueSortedKeys);
        // Reset selection when tenant changes
        setSelectedAllowedFeatures(new Set(uniqueSortedKeys));
      } finally {
        if (!cancelled) setLoadingAddModeTenantFeatures(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [open, isAddMode, selectedTenantId, selectedRoleKey, getTenantFeatureKeys]);

  // In Add mode, show airframes (tail numbers) based on selected tenant
  useEffect(() => {
    if (!open || !isAddMode) return;

    const normalizedRole = (selectedRoleKey ?? "").trim();
    const normalizedTenantId = (selectedTenantId ?? "").trim();

    // GlobalAdmin has no tenant assignment; airframe overrides are not applicable
    if (normalizedRole === "GlobalAdmin") {
      setAddModeEnabledTailNumbers([]);
      setSelectedAllowedAirframes(new Set());
      setLoadingAddModeTailNumbers(false);
      return;
    }

    // No tenant selected yet
    if (!normalizedTenantId) {
      setAddModeEnabledTailNumbers([]);
      setSelectedAllowedAirframes(new Set());
      setLoadingAddModeTailNumbers(false);
      return;
    }

    // Reset immediately on tenant change to avoid stale state
    setAddModeEnabledTailNumbers([]);
    setSelectedAllowedAirframes(new Set());

    let cancelled = false;

    const load = async () => {
      setLoadingAddModeTailNumbers(true);
      try {
        const tails = await getTailNumbersForTenant(normalizedTenantId);
        if (cancelled) return;

        const normalized = (tails ?? [])
          .map((t) => (t ?? "").trim())
          .filter((t) => Boolean(t));

        const uniqueSorted = Array.from(new Set(normalized)).sort((a, b) => a.localeCompare(b));

        setAddModeEnabledTailNumbers(uniqueSorted);
        setSelectedAllowedAirframes(new Set(uniqueSorted));
      } finally {
        if (!cancelled) setLoadingAddModeTailNumbers(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [open, isAddMode, selectedTenantId, selectedRoleKey, getTailNumbersForTenant]);

  // Load enabled airframes (tail numbers) + existing user overrides for each tenant mapping (Edit mode)
  useEffect(() => {
    if (!open) return;
    if (isAddMode) return;
    if (!formData?.entraObjectId) return;

    const activeTenantIds = mappingRows
      .filter((r) => !r.isDeleted)
      .filter((r) => r.role !== "GlobalAdmin")
      .map((r) => (r.tenantId ?? "").trim())
      .filter((id) => Boolean(id));

    const uniqueTenantIds = Array.from(new Set(activeTenantIds));
    if (uniqueTenantIds.length === 0) {
      setTenantAirframeSections({});
      setTenantAirframesError(null);
      return;
    }

    let cancelled = false;

    const loadTenantAirframes = async () => {
      setLoadingTenantAirframes(true);
      setTenantAirframesError(null);

      try {
        const overrideRows = await getUserAirframeOverrides(formData.entraObjectId);
        const disAllowByTenantId: Record<string, string[]> = {};
        overrideRows.forEach((row) => {
          const key = (row?.tenantId ?? "").trim();
          if (!key) return;
          disAllowByTenantId[key] = Array.isArray(row.disAllowAirframes) ? row.disAllowAirframes : [];
        });

        const tailNumberLists = await Promise.all(
          uniqueTenantIds.map(async (tenantId) => {
            const tails = await getTailNumbersForTenant(tenantId);
            return {
              tenantId,
              tails: (tails ?? [])
                .map((t) => (t ?? "").trim())
                .filter((t) => Boolean(t)),
            };
          }),
        );

        if (cancelled) return;

        setTenantAirframeSections((previous) => {
          const next: Record<string, TenantAirframeSectionState> = { ...previous };

          tailNumberLists.forEach(({ tenantId, tails }) => {
            const tenantName =
              tenants.find((t) => t.id === tenantId)?.name ??
              (user?.tenants ?? []).find((t) => t.tenantId === tenantId)?.tenantName ??
              tenantId;

            const enabledTailNumbers = Array.from(new Set(tails)).sort((a, b) => a.localeCompare(b));

            const disAllowed = (disAllowByTenantId[tenantId] ?? [])
              .map((t) => (t ?? "").trim())
              .filter((t) => Boolean(t));
            const disAllowedLower = new Set(disAllowed.map((t) => t.toLowerCase()));

            const existing = previous[tenantId];
            if (existing) {
              const intersectAllowed = enabledTailNumbers.filter((t) => existing.selectedAllowedTailNumbers.has(t));
              next[tenantId] = {
                tenantId,
                tenantName,
                enabledTailNumbers,
                selectedAllowedTailNumbers: new Set(intersectAllowed),
              };
              return;
            }

            const initialAllowed = enabledTailNumbers.filter((t) => !disAllowedLower.has(t.toLowerCase()));
            next[tenantId] = {
              tenantId,
              tenantName,
              enabledTailNumbers,
              selectedAllowedTailNumbers: new Set(initialAllowed),
            };
          });

          // Remove entries for tenants the user is no longer mapped to
          Object.keys(next).forEach((tenantId) => {
            if (!uniqueTenantIds.includes(tenantId)) {
              delete next[tenantId];
            }
          });

          return next;
        });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to load tenant airframes";
        setTenantAirframesError(message);
      } finally {
        if (!cancelled) setLoadingTenantAirframes(false);
      }
    };

    void loadTenantAirframes();

    return () => {
      cancelled = true;
    };
  }, [open, isAddMode, formData?.entraObjectId, mappingRows, tenants, user?.tenants, getTailNumbersForTenant, getUserAirframeOverrides]);

  // Load enabled features + existing user overrides for each tenant mapping (Edit mode)
  useEffect(() => {
    if (!open) return;
    if (isAddMode) return;
    if (!formData?.entraObjectId) return;

    const activeTenantIds = mappingRows
      .filter((r) => !r.isDeleted)
      .filter((r) => r.role !== "GlobalAdmin")
      .map((r) => (r.tenantId ?? "").trim())
      .filter((id) => Boolean(id));

    const uniqueTenantIds = Array.from(new Set(activeTenantIds));
    if (uniqueTenantIds.length === 0) {
      setTenantFeatureSections({});
      setTenantFeaturesError(null);
      return;
    }

    let cancelled = false;

    const loadTenantFeatures = async () => {
      setLoadingTenantFeatures(true);
      setTenantFeaturesError(null);

      try {
        const overrideRows = await getUserFeatureOverrides(formData.entraObjectId);
        const disAllowByTenantId: Record<string, string[]> = {};
        overrideRows.forEach((row) => {
          const key = (row?.tenantId ?? "").trim();
          if (!key) return;
          disAllowByTenantId[key] = Array.isArray(row.disAllowFeatures) ? row.disAllowFeatures : [];
        });

        const featureKeyLists = await Promise.all(
          uniqueTenantIds.map(async (tenantId) => {
            const keys = await getTenantFeatureKeys(tenantId);
            return {
              tenantId,
              keys: (keys ?? [])
                .map((k) => (k ?? "").trim())
                .filter((k) => Boolean(k)),
            };
          })
        );

        if (cancelled) return;

        setTenantFeatureSections((previous) => {
          const next: Record<string, TenantFeatureSectionState> = { ...previous };

          featureKeyLists.forEach(({ tenantId, keys }) => {
            const tenantName =
              tenants.find((t) => t.id === tenantId)?.name ??
              (user?.tenants ?? []).find((t) => t.tenantId === tenantId)?.tenantName ??
              tenantId;

            const enabledFeatureKeys = Array.from(new Set(keys)).sort((a, b) => a.localeCompare(b));

            const disAllowed = (disAllowByTenantId[tenantId] ?? [])
              .map((k) => (k ?? "").trim())
              .filter((k) => Boolean(k));
            const disAllowedLower = new Set(disAllowed.map((k) => k.toLowerCase()));

            const existing = previous[tenantId];
            if (existing) {
              const intersectAllowed = enabledFeatureKeys.filter((k) => existing.selectedAllowedFeatureKeys.has(k));
              next[tenantId] = {
                tenantId,
                tenantName,
                enabledFeatureKeys,
                selectedAllowedFeatureKeys: new Set(intersectAllowed),
              };
              return;
            }

            const initialAllowed = enabledFeatureKeys.filter((k) => !disAllowedLower.has(k.toLowerCase()));
            next[tenantId] = {
              tenantId,
              tenantName,
              enabledFeatureKeys,
              selectedAllowedFeatureKeys: new Set(initialAllowed),
            };
          });

          // Remove entries for tenants the user is no longer mapped to
          Object.keys(next).forEach((tenantId) => {
            if (!uniqueTenantIds.includes(tenantId)) {
              delete next[tenantId];
            }
          });

          return next;
        });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to load tenant features";
        setTenantFeaturesError(message);
      } finally {
        if (!cancelled) setLoadingTenantFeatures(false);
      }
    };

    void loadTenantFeatures();

    return () => {
      cancelled = true;
    };
  }, [open, isAddMode, formData?.entraObjectId, mappingRows, tenants, user?.tenants, getTenantFeatureKeys, getUserFeatureOverrides]);

  const toggleAllowedFeatureForTenant = (tenantId: string, featureKey: string) => {
    const normalizedTenantId = (tenantId ?? "").trim();
    const normalizedFeatureKey = (featureKey ?? "").trim();
    if (!normalizedTenantId || !normalizedFeatureKey) return;

    setTenantFeatureSections((prev) => {
      const current = prev[normalizedTenantId];
      if (!current) return prev;

      const nextAllowed = new Set(current.selectedAllowedFeatureKeys);
      if (nextAllowed.has(normalizedFeatureKey)) {
        nextAllowed.delete(normalizedFeatureKey);
      } else {
        nextAllowed.add(normalizedFeatureKey);
      }

      return {
        ...prev,
        [normalizedTenantId]: {
          ...current,
          selectedAllowedFeatureKeys: nextAllowed,
        },
      };
    });
  };

  const computeTenantDisAllowFeatures = (tenantId: string) => {
    const section = tenantFeatureSections[(tenantId ?? "").trim()];
    if (!section) return [] as string[];

    return section.enabledFeatureKeys
      .filter((k) => !section.selectedAllowedFeatureKeys.has(k))
      .filter((k) => Boolean((k ?? "").trim()));
  };

  const toggleAllowedAirframeForTenant = (tenantId: string, tailNumber: string) => {
    const normalizedTenantId = (tenantId ?? "").trim();
    const normalizedTailNumber = (tailNumber ?? "").trim();
    if (!normalizedTenantId || !normalizedTailNumber) return;

    setTenantAirframeSections((prev) => {
      const current = prev[normalizedTenantId];
      if (!current) return prev;

      const nextAllowed = new Set(current.selectedAllowedTailNumbers);
      if (nextAllowed.has(normalizedTailNumber)) {
        nextAllowed.delete(normalizedTailNumber);
      } else {
        nextAllowed.add(normalizedTailNumber);
      }

      return {
        ...prev,
        [normalizedTenantId]: {
          ...current,
          selectedAllowedTailNumbers: nextAllowed,
        },
      };
    });
  };

  const computeTenantDisAllowAirframes = (tenantId: string) => {
    const section = tenantAirframeSections[(tenantId ?? "").trim()];
    if (!section) return [] as string[];

    return section.enabledTailNumbers
      .filter((t) => !section.selectedAllowedTailNumbers.has(t))
      .filter((t) => Boolean((t ?? "").trim()));
  };

  const toggleAllowedFeature = (featureKey: string) => {
    const normalized = (featureKey ?? "").trim();
    if (!normalized) return;

    setSelectedAllowedFeatures((previous) => {
      const next = new Set(previous);
      if (next.has(normalized)) {
        next.delete(normalized);
      } else {
        next.add(normalized);
      }
      return next;
    });
  };

  const toggleAllowedAirframe = (tailNumber: string) => {
    const normalized = (tailNumber ?? "").trim();
    if (!normalized) return;

    setSelectedAllowedAirframes((previous) => {
      const next = new Set(previous);
      if (next.has(normalized)) {
        next.delete(normalized);
      } else {
        next.add(normalized);
      }
      return next;
    });
  };

  const showValidationMessages = (messages: string[]) => {
    const normalizedMessages = Array.from(
      new Set(
        messages
          .map((message) => (message ?? "").trim())
          .filter((message) => Boolean(message))
      )
    );

    setValidationMessages(normalizedMessages);
  };

  const computeDisAllowFeatures = () => {
    const allKeys = addModeEnabledFeatureKeys
      .map((k) => (k ?? "").trim())
      .filter((k) => Boolean(k));

    if (allKeys.length === 0) return [] as string[];

    return allKeys
      .filter((k) => !selectedAllowedFeatures.has(k))
      .filter((k, index, array) => array.indexOf(k) === index);
  };

  const computeDisAllowAirframes = () => {
    const allTails = addModeEnabledTailNumbers
      .map((t) => (t ?? "").trim())
      .filter((t) => Boolean(t));

    if (allTails.length === 0) return [] as string[];

    return allTails
      .filter((t) => !selectedAllowedAirframes.has(t))
      .filter((t, index, array) => array.indexOf(t) === index);
  };

  const handleSave = async () => {
    if (!formData) return;

    const nextValidationMessages: string[] = [];

    if (!formData.displayName?.trim()) {
      nextValidationMessages.push("Please provide a display name.");
    }

    if (isAddMode) {
      if (!formData.email) {
        nextValidationMessages.push("Please provide an email address.");
      }
      if (!selectedRoleKey) {
        nextValidationMessages.push("Please select a role.");
      }
      if (!selectedTenantId && selectedRoleKey !== "GlobalAdmin") {
        nextValidationMessages.push("Please select a tenant.");
      }
    }

    const toUtcStartOfDayIso = (date: Date) =>
      new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)).toISOString();

    const toUtcEndOfDayIso = (date: Date) =>
      new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)).toISOString();

    const hasStartDateUtc = startDateUtcInput instanceof Date;
    const hasEndDateUtc = endDateUtcInput instanceof Date;

    if (hasStartDateUtc !== hasEndDateUtc) {
      nextValidationMessages.push("Please provide both Start Date and End Date, or leave both blank.");
    }

    const startDateUtc = startDateUtcInput ? toUtcStartOfDayIso(startDateUtcInput) : undefined;
    const endDateUtc = endDateUtcInput ? toUtcEndOfDayIso(endDateUtcInput) : undefined;

    if (startDateUtc && endDateUtc && new Date(startDateUtc).getTime() > new Date(endDateUtc).getTime()) {
      nextValidationMessages.push("Start Date must be less than or equal to End Date.");
    }

    if (nextValidationMessages.length > 0) {
      showValidationMessages(nextValidationMessages);
      return;
    }

    setIsSaving(true);
    try {
      if (isAddMode) {
        await addUser({
          email: formData.email,
          displayName: formData.displayName,
          roleName: selectedRoleKey,
          tenantId: selectedTenantId || null,
          isEnabled: formData.status === "enabled",
          startDateUtc,
          endDateUtc,
          disAllowFeatures: computeDisAllowFeatures(),
          disAllowAirframes: computeDisAllowAirframes(),
        });

        const updates: UpdateUserRequest = {
          displayName: formData.displayName,
          status: formData.status,
        };
        await onSave(formData.id || formData.email, updates);
        onClose();
      } else {
        const isReAddScenario =
          isGlobalAdminRowRemoved &&
          mappingRows.some((r) => r.role === "GlobalAdmin" && r.isNew && !r.isDeleted);

        const tenantRoleMappings = mappingRows
          // In the re-add scenario, exclude new GlobalAdmin rows (net effect = keep, no re-assign needed)
          .filter((r) => !(r.role === "GlobalAdmin" && r.isNew && isReAddScenario))
          // Discard rows that were added and immediately removed (never persisted)
          .filter((r) => !r.isNew || !r.isDeleted)
          // For non-GlobalAdmin rows a tenantId is required; GlobalAdmin always has null tenantId
          .filter((r) => r.role === "GlobalAdmin" || (Boolean(r.tenantId) && Boolean(r.role)))
          .map((r) => ({
            tenantId: r.role === "GlobalAdmin" ? null : r.tenantId,
            role: r.role,
            isDelete: r.isDeleted,
            id: r.isNew ? undefined : r.id,
            disAllowFeatures:
              r.isDeleted || r.role === "GlobalAdmin" || !r.tenantId
                ? []
                : computeTenantDisAllowFeatures(r.tenantId),
            disAllowAirframes:
              r.isDeleted || r.role === "GlobalAdmin" || !r.tenantId
                ? []
                : computeTenantDisAllowAirframes(r.tenantId),
          }));

        const showGlobalAdminRow = isLoggedInGlobalAdmin && isActingAsGlobalAdmin;
        if (showGlobalAdminRow && globalAdminChecked !== initialGlobalAdminChecked.current) {
          tenantRoleMappings.push({
            tenantId: null,
            role: "GlobalAdmin",
            isDelete: isGlobalAdminRowRemoved,
            id: undefined,
            disAllowFeatures: [],
            disAllowAirframes: [],
          });
        }

        // Check for duplicate tenant mappings
        const tenantIds = tenantRoleMappings
          .filter((md) => md.isDelete === false)
          .map((m) => m.tenantId)
          .filter((id): id is string => Boolean(id));
        const seen: Record<string, boolean> = {};
        const duplicateIds: string[] = [];
        tenantIds.forEach((id) => {
          if (seen[id]) { duplicateIds.push(id); } else { seen[id] = true; }
        });
        if (duplicateIds.length > 0) {
          const uniqueDupNames = duplicateIds
            .map((id) => tenants.find((t) => t.id === id)?.name ?? id)
            .filter((v, i, a) => a.indexOf(v) === i);
          showValidationMessages([
            `A user can only have one role per tenant. Duplicate mapping found for: ${uniqueDupNames.join(", ")}.`,
          ]);
          setIsSaving(false);
          return;
        }
        const updates: UpdateUserRequest = {
          displayName: formData.displayName,
          status: formData.status,
          entraObjectId: formData.entraObjectId,
          email: formData.email,
          isEnabled: formData.status === "enabled",
          startDateUtc,
          endDateUtc,
          tenantRoleMappings,
        };

        await onSave(formData.entraObjectId, updates);
        onClose();
      }
    } catch (error) {
      console.error("Error saving user:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: keyof User, value: any) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleMappingRowChange = (rowId: string, updates: Partial<TenantRoleMappingRow>) => {
    setMappingRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...updates } : r)));
  };

  const handleAddMappingRow = () => {
    // Check if there's an empty row already (no tenant selected yet).
    // GlobalAdmin rows have an intentionally blank tenantId, so exclude them from this check.
    const hasEmptyRow = mappingRows.some((r) => r.isNew && !r.tenantId && r.role !== "GlobalAdmin");
    if (hasEmptyRow) {
      showValidationMessages(["Please complete the existing mapping before adding a new one."]);
      return;
    }

    setMappingRows((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        tenantId: isInTenantMode && currentTenantId ? currentTenantId : "",
        role: "",
        isEnabled: true,
        isNew: true,
        isDeleted: false,
      },
    ]);
  };

  const handleRemoveNewMappingRow = (rowId: string) => {
    setMappingRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const handleDeleteMappingRow = (rowId: string) => {
    setMappingRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? r.isNew
            ? { ...r, isDeleted: true }
            : { ...r, isDeleted: true, isEnabled: false }
          : r
      )
    );
  };

  const showAirframeOverrideTab = isInTenantMode ;
  const timeboundTabIndex = showAirframeOverrideTab ? 3 : 2;

  useEffect(() => {
    if (!open) return;
    const maxTabIndex = showAirframeOverrideTab ? 3 : 2;
    if (activeTab > maxTabIndex) setActiveTab(maxTabIndex);
  }, [activeTab, open, showAirframeOverrideTab]);

  if (!formData) return null;

  const showGlobalAdminRow = isEditMode && isLoggedInGlobalAdmin && isActingAsGlobalAdmin;
  const showGlobalAdminRoleInAddMode = isAddMode && isLoggedInGlobalAdmin && isActingAsGlobalAdmin;
  const isEditingSelf = Boolean(currentUserId) && currentUserId === formData.id;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#666" },
      "&:hover fieldset": { borderColor: "#999" },
      "&.Mui-focused fieldset": { borderColor: "hsl(19 101% 51% / 1)" },
    },
  };

  const readOnlyTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#2a2a2a",
      "& fieldset": { borderColor: "#383838" },
      "&:hover fieldset": { borderColor: "#383838" },
      "&.Mui-focused fieldset": { borderColor: "#383838" },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: "rgba(255,255,255,0.38)",
      WebkitTextFillColor: "rgba(255,255,255,0.38)",
      opacity: 1,
      cursor: "not-allowed",
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      color: "rgba(255,255,255,0.38)",
      opacity: 1,
    },
  };

  const selectSx = {
    color: "#fff",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#999" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(19 101% 51% / 1)" },
    "& .MuiSvgIcon-root": { color: "#fff" },
  };

  const disabledSelectReadableSx = {
    ...selectSx,
    "&.Mui-disabled": {
      opacity: 1,
      color: "#fff",
    },
    "& .MuiSelect-select.Mui-disabled": {
      WebkitTextFillColor: "#fff",
      color: "#fff",
      opacity: 1,
    },
    "& .MuiSvgIcon-root.Mui-disabled": {
      opacity: 1,
      color: "rgba(255,255,255,0.8)",
    },
  };

  const readOnlySelectSx = {
    backgroundColor: "#2a2a2a",
    cursor: "not-allowed",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#383838" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#383838" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#383838" },
    "&.Mui-disabled": { opacity: 1 },
    "& .MuiSelect-select.Mui-disabled": {
      WebkitTextFillColor: "rgba(255,255,255,0.38)",
      color: "rgba(255,255,255,0.38)",
      opacity: 1,
      cursor: "not-allowed",
    },
    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.38)" },
    "& .MuiSvgIcon-root.Mui-disabled": { color: "rgba(255,255,255,0.38)", opacity: 1 },
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#111",
          color: "#fff",
          border: "1px solid #333",
        },
      }}
    >
      <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
        {isAddMode ? "Add User" : "Edit User"}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1, borderBottom: "1px solid #333" }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="User form tabs"
            sx={{
              "& .MuiTab-root": {
                color: "rgba(255,255,255,0.75)",
                textTransform: "none",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#fff",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "hsl(19 101% 51% / 1)",
              },
            }}
          >
            <Tab label="User" value={0} {...a11yProps(0)} />
            <Tab label="Feature Override" value={1} {...a11yProps(1)} />
            {showAirframeOverrideTab && <Tab label="Airframe Override" value={2} {...a11yProps(2)} />}
            <Tab label="Timebound Override" value={timeboundTabIndex} {...a11yProps(timeboundTabIndex)} />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Stack spacing={3}>
            {/* Add-mode Tenant Dropdown */}
            {isAddMode && (
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    color: (isInTenantMode || selectedRoleKey === "GlobalAdmin")
                      ? "rgba(255,255,255,0.38)"
                      : "#fff",
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.38)" },
                  }}
                >
                  Tenant
                </InputLabel>
                <Select
                  value={selectedRoleKey === "GlobalAdmin" ? "" : selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  label="Tenant"
                  disabled={loadingDropdowns || isInTenantMode || selectedRoleKey === "GlobalAdmin"}
                  sx={(isInTenantMode || selectedRoleKey === "GlobalAdmin") ? readOnlySelectSx : selectSx}
                  endAdornment={loadingDropdowns ? <CircularProgress size={20} sx={{ color: "#fff", mr: 2 }} /> : null}
                >
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {/* Email Field */}
              <TextField
                label="Email"
                value={formData.email}
                fullWidth
                disabled={isIdentityFieldsReadOnly}
                onChange={
                  isIdentityFieldsReadOnly
                    ? undefined
                    : (e) => handleFieldChange("email", e.target.value)
                }
                InputProps={{ sx: { color: "#fff" } }}
                InputLabelProps={{ sx: { color: "#fff" } }}
                sx={isIdentityFieldsReadOnly ? readOnlyTextFieldSx : inputSx}
              />

              {/* Display Name Field */}
              <TextField
                label="Display Name"
                value={formData.displayName}
                onChange={(e) => handleFieldChange("displayName", e.target.value)}
                fullWidth
                InputProps={{ sx: { color: "#fff" } }}
                InputLabelProps={{ sx: { color: "#fff" } }}
                sx={inputSx}
              />

              {/* IsEnabled */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.status === "enabled"}
                    onChange={(e) =>
                      handleFieldChange("status", e.target.checked ? "enabled" : "disabled")
                    }
                    sx={{
                      color: "#fff",
                      "&.Mui-checked": {
                        color: "hsl(19 101% 51% / 1)",
                      },
                    }}
                  />
                }
                label="Enabled"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    color: "rgba(255,255,255,0.9)",
                  },
                  "& .MuiFormControlLabel-label.Mui-disabled": {
                    color: "rgba(255,255,255,0.55)",
                  },
                }}
              />
            </Box>

            {/* Add-mode Role Dropdown */}
            {isAddMode && (
              <FormControl fullWidth>
                <InputLabel sx={{ color: "#fff" }}>Role</InputLabel>
                <Select
                  value={selectedRoleKey}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setSelectedRoleKey(newRole);
                    if (newRole === "GlobalAdmin") {
                      setSelectedTenantId("");
                    }
                  }}
                  label="Role"
                  disabled={loadingDropdowns}
                  sx={selectSx}
                  endAdornment={loadingDropdowns ? <CircularProgress size={20} sx={{ color: "#fff", mr: 2 }} /> : null}
                >
                  {roles
                    .filter((r) => showGlobalAdminRoleInAddMode || r.role !== "GlobalAdmin")
                    .map((r) => (
                      <MenuItem key={r.id} value={r.role}>
                        {r.role}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            {/* Tenant/Role Mapping - Edit mode */}
            {isEditMode && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ color: "hsl(19 101% 51% / 1)", fontWeight: 600 }}>
                    Tenant/Role Mapping
                  </Typography>

                  {!isInTenantMode && (
                    <Tooltip title="Add mapping">
                      <IconButton
                        size="small"
                        onClick={handleAddMappingRow}
                        sx={{
                          color: "hsl(19 101% 51% / 1)",
                          border: "1px solid #333",
                          borderRadius: 1,
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                        }}
                        aria-label="Add tenant role mapping"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <TableContainer
                  component={Paper}
                  sx={{ backgroundColor: "#111", border: "1px solid #333" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#fff", borderBottom: "1px solid #333", fontWeight: 700 }}>
                          Tenant
                        </TableCell>
                        <TableCell sx={{ color: "#fff", borderBottom: "1px solid #333", fontWeight: 700 }}>
                          Role
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#fff",
                            borderBottom: "1px solid #333",
                            fontWeight: 700,
                            width: 160,
                          }}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(!isGlobalAdminRowRemoved && showGlobalAdminRow && globalAdminChecked) ? (
                        <TableRow>
                          <TableCell sx={{ borderBottom: "1px solid #222" }}>
                            <TextField
                              value=""
                              size="small"
                              disabled
                              fullWidth
                              sx={readOnlyTextFieldSx}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #222" }}>
                            <TextField
                              value="GlobalAdmin"
                              size="small"
                              disabled
                              fullWidth
                              sx={readOnlyTextFieldSx}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #222" }}>
                            <Tooltip
                              title={
                                isEditingSelf
                                  ? "You cannot change your own Global Admin assignment"
                                  : globalAdminChecked
                                    ? "Remove Global Admin"
                                    : "Not a Global Admin"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setGlobalAdminChecked(false);
                                    setIsGlobalAdminRowRemoved(true);
                                  }}
                                  disabled={isEditingSelf || !globalAdminChecked}
                                  aria-label="Remove Global Admin"
                                  sx={{
                                    color: "rgba(255,255,255,0.85)",
                                    border: "1px solid #333",
                                    borderRadius: 1,
                                    "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                                    "&.Mui-disabled": {
                                      opacity: 1,
                                      color: "rgba(255,255,255,0.35)",
                                    },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ) : null}

                      {(mappingRows.length === 0 && (!showGlobalAdminRow || isGlobalAdminRowRemoved)) ? (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ borderBottom: "1px solid #222", color: "rgba(255,255,255,0.8)" }}>
                            No tenant/role mappings. Click + to add.
                          </TableCell>
                        </TableRow>
                      ) : null}

                      {mappingRows.filter((row) => !row.isDeleted).map((row) => (
                        <TableRow key={row.id}>
                          <TableCell sx={{ borderBottom: "1px solid #222" }}>
                            <FormControl fullWidth size="small">
                              <Select
                                value={row.role === "GlobalAdmin" ? "" : row.tenantId}
                                onChange={(e) =>
                                  handleMappingRowChange(row.id, { tenantId: String(e.target.value) })
                                }
                                displayEmpty
                                disabled={loadingDropdowns || row.role === "GlobalAdmin" || (!isActingAsGlobalAdmin && (!row.isNew || isInTenantMode))}
                                sx={
                                  row.role === "GlobalAdmin"
                                    ? readOnlySelectSx
                                    : (row.isNew || isActingAsGlobalAdmin) && !isInTenantMode
                                      ? selectSx
                                      : disabledSelectReadableSx
                                }
                              >
                                <MenuItem value="">
                                  <em>Select tenant</em>
                                </MenuItem>
                                {tenants.map((t) => (
                                  <MenuItem key={t.id} value={t.id}>
                                    {t.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #222" }}>
                            <FormControl fullWidth size="small">
                              <Select
                                value={row.role}
                                onChange={(e) => {
                                  const newRole = String(e.target.value);
                                  if (newRole === "GlobalAdmin") {
                                    setIsGlobalAdminRowRemoved(false)

                                    const topRowActive =
                                      showGlobalAdminRow && globalAdminChecked && !isGlobalAdminRowRemoved;
                                    const otherGlobalAdminRow = mappingRows.some(
                                      (r) => r.id !== row.id && r.role === "GlobalAdmin" && !r.isDeleted
                                    );
                                    if (topRowActive || otherGlobalAdminRow) {
                                        showValidationMessages([
                                          "A user can only have one Global Admin assignment. Remove the existing Global Admin entry first.",
                                        ]);
                                      return;
                                    }
                                  }
                                  handleMappingRowChange(row.id, {
                                    role: newRole,
                                    ...(newRole === "GlobalAdmin" ? { tenantId: "" } : {}),
                                  });
                                }}
                                displayEmpty
                                disabled={loadingDropdowns || (!isActingAsGlobalAdmin && !row.isNew && !(isInTenantMode && row.tenantId === currentTenantId))}
                                sx={(row.isNew || isActingAsGlobalAdmin || (isInTenantMode && row.tenantId === currentTenantId)) ? selectSx : disabledSelectReadableSx}
                              >
                                <MenuItem value="">
                                  <em>Select role</em>
                                </MenuItem>
                                {roles
                                  .filter((r) => r.role !== (isInTenantMode ? "GlobalAdmin" : ""))
                                  .map((r) => (
                                    <MenuItem key={r.id} value={r.role}>
                                      {r.role}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #222" }}>
                            {row.isNew ? (
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveNewMappingRow(row.id)}
                                  aria-label="Remove tenant role mapping"
                                  sx={{
                                    color: "rgba(255,255,255,0.85)",
                                    border: "1px solid #333",
                                    borderRadius: 1,
                                    "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteMappingRow(row.id)}
                                aria-label="Delete tenant role mapping"
                                sx={{
                                  color: "rgba(255,255,255,0.85)",
                                  border: "1px solid #333",
                                  borderRadius: 1,
                                  "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Alert
              severity="info"
              icon={<InfoIcon fontSize="small" />}
              sx={{
                backgroundColor: "rgba(33, 150, 243, 0.1)",
                color: "#90caf9",
                border: "1px solid rgba(33, 150, 243, 0.3)",
                "& .MuiAlert-icon": {
                  color: "#90caf9",
                },
              }}
            >
              Role permissions will be applied after the user logs in again.
            </Alert>
            {!isInTenantMode && !isEditMode && (
              <Alert
                severity="info"
                icon={<InfoIcon fontSize="small" />}
                sx={{
                  backgroundColor: "rgba(33, 150, 243, 0.1)",
                  color: "#90caf9",
                  border: "1px solid rgba(33, 150, 243, 0.3)",
                  "& .MuiAlert-icon": {
                    color: "#90caf9",
                  },
                }}
              >
                With the Global Admin role, tenant assignment is not permitted.
              </Alert>
            )}
          </Stack>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {isAddMode ? (
            <Stack spacing={2}>
              {(loadingDropdowns || loadingAddModeTenantFeatures) ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>Loading features...</Typography>
                </Box>
              ) : null}

              {(!loadingDropdowns && !loadingAddModeTenantFeatures && addModeEnabledFeatureKeys.length > 0) ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {addModeEnabledFeatureKeys.map((featureKey) => {
                    const normalizedKey = (featureKey ?? "").trim();
                    if (!normalizedKey) return null;

                    const feature = availableFeatures.find(
                      (f) => String(f?.name ?? "").trim().toLowerCase() === normalizedKey.toLowerCase()
                    );

                    const isChecked = selectedAllowedFeatures.has(normalizedKey);

                    return (
                      <FormControlLabel
                        key={normalizedKey}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => toggleAllowedFeature(normalizedKey)}
                            sx={{
                              color: "#fff",
                              "&.Mui-checked": {
                                color: "hsl(19 101% 51% / 1)",
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography sx={{ color: "#fff", fontWeight: 500 }}>
                              {normalizedKey}
                            </Typography>
                            {feature?.description ? (
                              <Typography sx={{ color: "#aaa", fontSize: "0.85rem" }}>
                                {feature.description}
                              </Typography>
                            ) : null}
                          </Box>
                        }
                        sx={{ alignItems: "flex-start", width: "100%", ml: 0 }}
                      />
                    );
                  })}
                </Box>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {loadingTenantFeatures ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>Loading tenant features...</Typography>
                </Box>
              ) : null}

              {tenantFeaturesError ? (
                <Alert severity="error" sx={{ backgroundColor: "rgba(244, 67, 54, 0.1)", border: "1px solid rgba(244, 67, 54, 0.3)" }}>
                  {tenantFeaturesError}
                </Alert>
              ) : null}

              {!loadingTenantFeatures ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {Object.values(tenantFeatureSections).length === 0 ? (
                    <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                      No tenant mappings found for this user.
                    </Typography>
                  ) : null}

                  {Object.values(tenantFeatureSections).map((section) => (
                    <Box
                      key={section.tenantId}
                      sx={{
                        border: "1px solid #333",
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: "rgba(255,255,255,0.02)",
                      }}
                    >
                      <Typography sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                        {section.tenantName}
                      </Typography>

                      {section.enabledFeatureKeys.length === 0 ? (
                        <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                          No features are enabled for this tenant.
                        </Typography>
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {section.enabledFeatureKeys.map((featureKey) => {
                            const isChecked = section.selectedAllowedFeatureKeys.has(featureKey);
                            return (
                              <FormControlLabel
                                key={featureKey}
                                control={
                                  <Checkbox
                                    checked={isChecked}
                                    onChange={() => toggleAllowedFeatureForTenant(section.tenantId, featureKey)}
                                    sx={{
                                      color: "#fff",
                                      "&.Mui-checked": { color: "hsl(19 101% 51% / 1)" },
                                    }}
                                  />
                                }
                                label={<Typography sx={{ color: "#fff" }}>{featureKey}</Typography>}
                                sx={{ alignItems: "flex-start", width: "100%", ml: 0 }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Stack>
          )}

          <Alert
            severity="info"
            icon={<InfoIcon fontSize="small" />}
            sx={{
              mt: 2,
              backgroundColor: "rgba(33, 150, 243, 0.1)",
              color: "#90caf9",
              border: "1px solid rgba(33, 150, 243, 0.3)",
              "& .MuiAlert-icon": {
                color: "#90caf9",
              },
            }}
          >
            Feature override will be applied after the user logs in again.
          </Alert>
        </TabPanel>

        <TabPanel value={activeTab} index={timeboundTabIndex}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <DatePicker
                  label="Start Date"
                  value={parseDate(startDateUtcInput)}
                  onChange={(value) => setStartDateUtcInput(value)}
                  slotProps={{ ...darkDatePickerSlotProps, textField: { ...darkDatePickerSlotProps.textField, fullWidth: true } }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <DatePicker
                  label="End Date"
                  value={parseDate(endDateUtcInput)}
                  onChange={(value) => setEndDateUtcInput(value)}
                  slotProps={{ ...darkDatePickerSlotProps, textField: { ...darkDatePickerSlotProps.textField, fullWidth: true } }}
                />
              </Box>
            </Stack>
          </LocalizationProvider>

          <Alert
            severity="info"
            icon={<InfoIcon fontSize="small" />}
            sx={{
              mt: 2,
              backgroundColor: "rgba(33, 150, 243, 0.1)",
              color: "#90caf9",
              border: "1px solid rgba(33, 150, 243, 0.3)",
              "& .MuiAlert-icon": {
                color: "#90caf9",
              },
            }}
          >
            Timebound override will be applied after the user logs in again.
          </Alert>
        </TabPanel>

        {showAirframeOverrideTab ? (
          <TabPanel value={activeTab} index={2}>
            {isAddMode ? (
              <Stack spacing={2}>
                {(loadingDropdowns || loadingAddModeTailNumbers) ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                    <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>Loading tail numbers...</Typography>
                  </Box>
                ) : null}

                {(!loadingDropdowns && !loadingAddModeTailNumbers && addModeEnabledTailNumbers.length > 0) ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {addModeEnabledTailNumbers.map((tail) => {
                      const normalizedTail = (tail ?? "").trim();
                      if (!normalizedTail) return null;

                      const isChecked = selectedAllowedAirframes.has(normalizedTail);

                      return (
                        <FormControlLabel
                          key={normalizedTail}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleAllowedAirframe(normalizedTail)}
                              sx={{
                                color: "#fff",
                                "&.Mui-checked": {
                                  color: "hsl(19 101% 51% / 1)",
                                },
                              }}
                            />
                          }
                          label={<Typography sx={{ color: "#fff", fontWeight: 500 }}>{normalizedTail}</Typography>}
                          sx={{ alignItems: "center", width: "100%", ml: 0 }}
                        />
                      );
                    })}
                  </Box>
                ) : null}
              </Stack>
            ) : (
              <Stack spacing={2}>
                {loadingTenantAirframes ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                    <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>Loading tenant airframes...</Typography>
                  </Box>
                ) : null}

                {tenantAirframesError ? (
                  <Alert severity="error" sx={{ backgroundColor: "rgba(244, 67, 54, 0.1)", border: "1px solid rgba(244, 67, 54, 0.3)" }}>
                    {tenantAirframesError}
                  </Alert>
                ) : null}

                {!loadingTenantAirframes ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {Object.values(tenantAirframeSections).length === 0 ? (
                      <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                        No tenant mappings found for this user.
                      </Typography>
                    ) : null}

                    {Object.values(tenantAirframeSections).map((section) => (
                      <Box
                        key={section.tenantId}
                        sx={{
                          border: "1px solid #333",
                          borderRadius: 1,
                          p: 2,
                          backgroundColor: "rgba(255,255,255,0.02)",
                        }}
                      >
                        <Typography sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                          {section.tenantName}
                        </Typography>

                        {section.enabledTailNumbers.length === 0 ? (
                          <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                            No airframes are enabled for this tenant.
                          </Typography>
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {section.enabledTailNumbers.map((tail) => {
                              const isChecked = section.selectedAllowedTailNumbers.has(tail);
                              return (
                                <FormControlLabel
                                  key={tail}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={() => toggleAllowedAirframeForTenant(section.tenantId, tail)}
                                      sx={{
                                        color: "#fff",
                                        "&.Mui-checked": { color: "hsl(19 101% 51% / 1)" },
                                      }}
                                    />
                                  }
                                  label={<Typography sx={{ color: "#fff" }}>{tail}</Typography>}
                                  sx={{ alignItems: "center", width: "100%", ml: 0 }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Stack>
            )}

            <Alert
              severity="info"
              icon={<InfoIcon fontSize="small" />}
              sx={{
                mt: 2,
                backgroundColor: "rgba(33, 150, 243, 0.1)",
                color: "#90caf9",
                border: "1px solid rgba(33, 150, 243, 0.3)",
                "& .MuiAlert-icon": {
                  color: "#90caf9",
                },
              }}
            >
              Airframe override will be applied after the user logs in again.
            </Alert>
          </TabPanel>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onClose}
          disabled={isSaving}
          sx={{
            color: "#fff",
            borderColor: "#666",
            "&:hover": {
              borderColor: "#999",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            backgroundColor: "hsl(19 101% 51% / 1)",
            "&:hover": {
              backgroundColor: "hsl(19 101% 41% / 1)",
            },
          }}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>

      {/* Validation message dialog */}
      <Dialog
        open={validationMessages.length > 0}
        onClose={() => setValidationMessages([])}
        PaperProps={{
          sx: {
            backgroundColor: "#111",
            color: "#fff",
            border: "1px solid #333",
          },
        }}
      >
        <DialogTitle sx={{ color: "#f44336" }}>
          Validation Errors
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {validationMessages.map((message) => (
              <Typography key={message} sx={{ color: "#fff" }}>
                {message}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setValidationMessages([])}
            sx={{
              backgroundColor: "hsl(19 101% 51% / 1)",
              "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserFormDialog;
